import React, { useState } from "react";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Checkbox } from "@shared-components/shadcn/checkbox";
import { Button } from "@shared-components/shadcn/button";
import _ from "lodash";
import { formatGranularDateTick } from "./metric-utils";
import {
  AiProfileModelEnumValues,
  getModelMetadata,
  type AiProfileModelEnumType,
} from "@shared-utils/ai/configuration/ai-model-config";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";

const chartConfig: ChartConfig = {
  count: {
    label: "AI models count in chats",
  },
};

interface AiModelsCountInChatsProps {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
}

const [reasoningModels, nonReasoningModels] = _.partition(
  AiProfileModelEnumValues,
  (model) => getModelMetadata(model).isReasoning,
);

// Define which models are active by default
const INITIAL_VISIBLE_MODELS = new Set<AiProfileModelEnumType>([
  "GPT_4o",
  "GPT_4.1",
  "GPT_o4_MINI",
  "GPT_o3_MINI",
  "GPT_o3",
]);

export const AiModelsCountInChats = (props: AiModelsCountInChatsProps) => {
  // Query
  const coreMetricsQuery = api.coreMetrics.aiModelTypesCountInChats.useQuery({
    startDate: props.startDate,
    endDate: props.endDate,
    granularity: props.granularity,
  });
  const chartData = coreMetricsQuery.data?.chartData ?? [];

  const [visibleModels, setVisibleModels] = useState(INITIAL_VISIBLE_MODELS);

  const handleToggle = (model: AiProfileModelEnumType) => {
    setVisibleModels((current) => {
      if (current.has(model)) {
        current.delete(model);
      } else {
        current.add(model);
      }
      return new Set(current);
    });
  };

  const allModels = AiProfileModelEnumValues;

  const normalizedChartData = chartData;

  const modelTotals =
    coreMetricsQuery.data?.modelTotals ??
    ({} as Record<AiProfileModelEnumType, number>);

  const pieChartData = allModels
    .filter((model) => (modelTotals[model] ?? 0) > 0)
    .map((model) => {
      const metadata = getModelMetadata(model);
      return {
        name: metadata.name,
        value: modelTotals[model],
        color: metadata.color,
      };
    });

  const handleEnablePieModels = () => {
    const enabledModelsList = pieChartData.map((entry) => entry.name);

    setVisibleModels(new Set(enabledModelsList));
  };

  return (
    <div>
      <h2 className="mb-4">{"AI models count in chats"}</h2>
      <div className="mb-4 flex flex-col gap-8 sm:flex-row sm:gap-8">
        <div>
          <div className="mb-1 text-xs">{"Reasoning models"}</div>
          <div className="flex flex-wrap gap-3">
            {reasoningModels.map((model) => (
              <ModelLabel
                key={model}
                model={model}
                visibleModels={visibleModels}
                handleToggle={handleToggle}
              />
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 text-xs">{"Non-reasoning models"}</div>
          <div className="flex flex-wrap gap-3">
            {nonReasoningModels.map((model) => (
              <ModelLabel
                key={model}
                model={model}
                visibleModels={visibleModels}
                handleToggle={handleToggle}
              />
            ))}
            <Button onClick={handleEnablePieModels}>
              {"Enable models for selected time"}
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-8 flex w-full items-start">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] flex-1"
        >
          <LineChart data={normalizedChartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              fill="#006e96"
              minTickGap={32}
              tickFormatter={(value: string) =>
                formatGranularDateTick(value, props.granularity)
              }
            />
            <YAxis
              yAxisId="count"
              interval={1}
              orientation="left"
              domain={[0, "auto"]}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="w-[150px]"
                  nameKey="date"
                  labelFormatter={(value: string) => value}
                />
              }
            />
            <Legend />
            {allModels
              .filter((model) => visibleModels.has(model))
              .map((model) => {
                const metadata = getModelMetadata(model);
                const key = `modelData.${model}`;
                return (
                  <Line
                    key={key}
                    yAxisId="count"
                    type="monotone"
                    dataKey={key}
                    name={metadata.displayName}
                    stroke={metadata.color}
                    strokeWidth={2}
                    dot={false}
                  />
                );
              })}
          </LineChart>
        </ChartContainer>
      </div>
      <div className="flex h-full items-center justify-center">
        <PieChart width={600} height={250}>
          <Pie
            data={pieChartData.filter((entry) => visibleModels.has(entry.name))}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={60}
            label={({ name, value }) => `${name}: (${value})`}
          >
            {pieChartData
              .filter((entry) => visibleModels.has(entry.name))
              .map((entry, idx) => (
                <Cell key={`cell-${idx}`} fill={entry.color} />
              ))}
          </Pie>
          <RechartsTooltip />
          <Legend />
        </PieChart>
      </div>
    </div>
  );
};

const ModelLabel = (props: {
  model: AiProfileModelEnumType;
  visibleModels: Set<AiProfileModelEnumType>;
  handleToggle: (model: AiProfileModelEnumType) => void;
}) => {
  return (
    <label
      key={props.model}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
    >
      <Checkbox
        checked={props.visibleModels.has(props.model)}
        onCheckedChange={() => props.handleToggle(props.model)}
        className="accent-current"
      />
      <span>{getModelMetadata(props.model).displayName}</span>
    </label>
  );
};
